import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { LeftSideNavComponent } from '../../../shared/layout/app/left-menu/left-side-nav.component';
import { HeaderComponent } from '../../../shared/layout/app/header/header.component';
import { DeviceTypeService } from '../../../core/services/device-type.service';
import { DigitalCatalogueBllService } from '../digital-catalogue-bll.service';
import { ProfileBllService } from '../../profile/profile-bll.service';
import { UserService } from '../../../core/services/user.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '../../../shared/components/loader/loader.service';
import { ParamsToGetProductsList, ProductListResponseModel } from '../digital-catalogue.model';
import { APIErrorModel, BaseResponse } from '../../../shared/models/base-response.model';
import { UserEventStatusRespModel } from '../../profile/profile.model';
import { CommonModule } from '@angular/common';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { FormsModule } from '@angular/forms';
import { EventDetailsResponseModel } from '../../home/home.model';
import { HomeBllService } from '../../home/home-bll.service';

@Component({
  selector: 'app-catalogue-product-wishlist',
  imports: [LeftSideNavComponent, HeaderComponent, CommonModule, InfiniteScrollDirective, FormsModule],
  templateUrl: './catalogue-product-wishlist.component.html',
  styleUrl: './catalogue-product-wishlist.component.scss'
})
export class CatalogueProductWishlistComponent implements OnInit {
  @ViewChild("searchInput") searchInput!: ElementRef;
  isMobileDevice: boolean = false;
  companiesCount: number = 0;
  productsCount: number = 0;
  productList: ProductListResponseModel['list'] = [];


  /** Parameters to fetch product list */
  paramsToFetchData: ParamsToGetProductsList = {
    shuffled_index1: 0,
    shuffled_index2: 0,
    max_id: 0,
    currentpage: 1,
    is_favourite: 1,
    search: ''
  };

  /** pagination properties */
  totalCount = 0;
  pagesCount = 1;
  startItem = 0;
  endItem = 0;
  isDataLoaded = false; // initially false
  searchText: string = "";
  isSearchOpen = false;

  isLoadingMore: boolean = false;
  isLoading: boolean = false;      // Loader for initial API call

  /**
   * User profile data
   */
  userProfile: UserEventStatusRespModel["user_profile"] | null = null;
  searchDebounce: ReturnType<typeof setTimeout> | null = null;
  fromWishlist: boolean = true;
  
  eventDetails: EventDetailsResponseModel | null = null;
  
  constructor(
    private deviceService: DeviceTypeService,
    private catalogueBllService: DigitalCatalogueBllService,
    private profileBll: ProfileBllService,
    private userService: UserService,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private router: Router,
    private homeBll: HomeBllService
  ) { }

  ngOnInit(): void {
    this.isMobileDevice = this.deviceService.isMobile();
    const searchParam = this.route.snapshot.queryParamMap.get('search') || '';
    this.searchText = searchParam;
    this.resetParametersToFetchData(searchParam);

    /**
     * Subscribe to query parameters to get the pavilion code
     */

    this.callfetchProductList(this.paramsToFetchData);
    this.fetchUserEventStatus(); // Fetch user profile on init
    this.loadEventDetails();
  }

  /**
   * @description Fetches the user's event status and updates the user profile and service accordingly.
   */
  fetchUserEventStatus() {
    this.profileBll
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusRespModel>) => {
        this.userProfile = res.data.user_profile;
        if (this.userProfile) {
          const previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
          const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
          this.userService.setUser({
            name: userName,
            profileImg: previewLogo,
            mobile: this.userProfile.mobile,
            state: this.userProfile.state,
            city: this.userProfile.city,
            hasUnassignedTickets: res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets: res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 || res.data.event_my_tickets.tickets_assigned_for_me > 0,
          });
        }
      })
      .catch((err: APIErrorModel) => {
        console.error("Error fetching user event status:", err);
      })
  }

  /**
   * Fetch product list from the catalogue
   * @return void
   */
  callfetchProductList(params?: ParamsToGetProductsList, append = false) {
    if (append) {
      this.isLoadingMore = true;  // show spinner for infinite scroll
    } else {
      this.isLoading = true;      // show global loader for first load
      this.loader.show();
    }
    this.catalogueBllService.fetchProductList(params, this.fromWishlist)
      .then((res) => {
        const data = res.data;

        this.productList = append ? [...this.productList, ...data.list] : data.list;

        this.startItem = data.startitem;
        this.endItem = data.enditem;
        this.totalCount = data.totalcount;
        this.pagesCount = data.pagescount;
        this.paramsToFetchData.currentpage = data.currentpage;
        this.paramsToFetchData.max_id = data.max_id;
        this.paramsToFetchData.shuffled_index1 = data.shuffled_index1;
        this.paramsToFetchData.shuffled_index2 = data.shuffled_index2;

        this.companiesCount = data.companies_count;
        this.productsCount = data.products_count;
        this.isDataLoaded = true; // set to true after data is loaded       
      })
      .catch((err: APIErrorModel) => {
        this.isDataLoaded = true; // set to true even if there's an error
        this.loader.hide();  // stop loader in case of error as well
        if (err.status === 404) {
          this.router.navigate(['/catalogue/pavilion-list']);
        }
      }).finally(() => {
        // hide loader/spinner depending on call type
        if (append) {
          this.isLoadingMore = false;
        } else {
          this.isLoading = false;
          this.loader.hide();
        }
      });
  }

  /**
 * Navigates to the product details page.
 * @param productId The ID of the product to view.
 */
  viewProductDetails(productId: string | null): void {
    this.router.navigate(
      ['/catalogue/product-details', productId]
    );
  }

  /**
   * Handles the page change event.
   * @param page The new page number.
   */
  onPageChange(page: number): void {
    this.loader.show();

    const params = {
      ...this.paramsToFetchData,
      currentpage: page
    }

    this.callfetchProductList(params);
  }

  // Navigate to companies list page
  routeTocompaniesWishList(): void {
    if (this.paramsToFetchData.search){
      this.router.navigate(['/catalogue/company-wishlist'], { queryParams: { search: this.paramsToFetchData.search }} );
    } else {
      this.router.navigate(['/catalogue/company-wishlist']);
    }
  }

  onClickBack(): void {
    window.history.back();
  }

  // Navigate to top 
  private scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  // Triggered when user scrolls down
  onScrollDown() {
    // Prevent multiple simultaneous calls
    if (this.isLoading || this.isLoadingMore) return;

    if (!this.paramsToFetchData.currentpage) return;

    // Only call API if there are more pages
    if (this.paramsToFetchData.currentpage < this.pagesCount) {
      const nextParams: ParamsToGetProductsList = {
        ...this.paramsToFetchData,
        currentpage: this.paramsToFetchData.currentpage + 1,
        shuffled_index1: this.paramsToFetchData.shuffled_index1,
        shuffled_index2: this.paramsToFetchData.shuffled_index2,
      };

      // Use load more spinner for infinite scroll
      this.callfetchProductList(nextParams, true); // append items
    }
  }

  goToTicketPage(){
    this.router.navigate(["reg/select-ticket"]);
  }

  /**
   * Open floating search
   */
  openFloatingSearch() {
    this.isSearchOpen = true;

    // Focus textbox after animation
    if (this.searchInput) {
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 300);
    }
  }
   /**
   * Close floating search
   */
   closeFloatingSearch() {
    this.isSearchOpen = false;
    this.searchText = "";
    if ("search" in this.paramsToFetchData) {
      delete this.paramsToFetchData.search;
    }

    // Reset page number when closing search
    this.paramsToFetchData.currentpage = 1;
    this.callfetchProductList(this.paramsToFetchData);
    
  }

  private performSearch(immediate: boolean = false): void {
    const search = this.searchText.trim();
    
    // Reset parameters for new search
    this.resetParametersToFetchData(search);

    if (immediate) {
      // Don’t call API if empty search
      if (!search) {
        if ("search" in this.paramsToFetchData) {
          delete this.paramsToFetchData.search;
        }
        return;
      }
      this.callfetchProductList(this.paramsToFetchData);
      // Clear debounce so API is not triggered twice
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }
    } else {
      // Clear previous timer
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }
      this.searchDebounce = setTimeout(() => {
        this.callfetchProductList(this.paramsToFetchData);
      }, 500); // 500ms debounce
    }
  }
  /**
   * Handles search input with debounce
   */
  onSearchInput() {
    this.performSearch(false);
  }
  /**
   * Handles search icon click (immediate search)
   */
  onSearchIconClick() {
    this.performSearch(true);
  }

  resetParametersToFetchData(search?: string) {
    this.paramsToFetchData = {
      shuffled_index1: 0,
      shuffled_index2: 0,
      max_id: 0,
      currentpage: 1,
      search: search,
      is_favourite: 1
    }
  }

   loadEventDetails() {
    this.homeBll.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;      
      
         this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });
        
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }

}
